import { useRef, useState } from "react";
import { toast } from "sonner";
import { adminRefundBooking, api, extractApiError } from "@/lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_COLORS = { succeeded: "#2E7D5B", pending: "#B8962B", requires_action: "#B8962B", failed: "#A01E2A" };
const PORTION_LABEL = { service: "Service (Bar / DJ / Nightclub)", supply: "Social Supply custom items" };
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const Badge = ({ status }) => (
  <span className="font-label text-[0.55rem] px-2 py-0.5" style={{ background: STATUS_COLORS[status] || "#555", color: "#F5F1E8" }}>
    {status === "requires_action" ? "PENDING" : (status || "").toUpperCase()}
  </span>
);

const History = ({ b }) => (
  <div className="mt-4 space-y-1 text-xs" data-testid="refund-history">
    {(b.refunds || []).map((r, i) => (
      <div key={`r${i}`} className="flex flex-wrap gap-2 items-center" data-testid={`refund-row-${i}`}>
        <Badge status={r.status || "succeeded"} /> {money(r.amount)} · {PORTION_LABEL[r.portion || "service"]} · {new Date(r.at).toLocaleString()}
        · Stripe {r.refund_id} · payment {r.payment_intent}{r.reason && ` · “${r.reason}”`}
      </div>
    ))}
    {(b.refund_attempts || []).map((a, i) => (
      <div key={`a${i}`} className="flex flex-wrap gap-2 items-center" style={{ color: "var(--ss-muted)" }} data-testid={`refund-failed-${i}`}>
        <Badge status="failed" /> {money(a.amount)} attempt · {new Date(a.at).toLocaleString()} · {a.error}
      </div>
    ))}
  </div>
);

export const RefundPanel = ({ d, token, onDone }) => {
  const r = d.refund;
  const b = d.booking;
  const portions = Object.entries(r.portions).filter(([, p]) => p.enabled && (p.available > 0 || p.refunded > 0));
  const [portion, setPortion] = useState(portions[0]?.[0] || "service");
  const p = r.portions[portion];
  const [amount, setAmount] = useState(p?.recommended || "");
  const [reason, setReason] = useState("");
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const reqId = useRef(null);
  const availAtReview = useRef(0);
  const amt = Number(amount) || 0;
  const valid = amt > 0 && amt <= (p?.available || 0) && reason.trim().length >= 3;

  const pick = (k) => { setPortion(k); setAmount(r.portions[k].recommended || ""); setReview(false); setError(""); };
  const openReview = () => { reqId.current = newId(); availAtReview.current = p.available; setError(""); setReview(true); };
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await adminRefundBooking(token, b.id, { amount: amt, reason: reason.trim(), portion, request_id: reqId.current });
      toast.success(`Refund of ${money(res.refunded_total ?? amt)} processed`);
      setReview(false); setReason(""); setAmount(""); onDone();
    } catch (e) {
      const msg = extractApiError(e, "Refund failed");
      setError(msg); toast.error(msg); onDone();
    }
    setBusy(false);
  };
  const refresh = async () => {
    try { await api.post(`/admin/bookings/${b.id}/refunds/refresh`, {}, { headers: { "X-Admin-Password": token } }); onDone(); }
    catch (e) { toast.error(extractApiError(e)); }
  };

  return (
    <div className="p-5 border" style={{ borderColor: "var(--ss-border)" }} data-testid="refund-panel">
      <div className="flex flex-wrap justify-between gap-2 mb-3">
        <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-rust)" }}>Refund (manual approval)</div>
        <div className="text-xs" data-testid="refund-state">{r.refund_state} · Paid {money(r.total_paid)} · Refunded {money(r.already_refunded)} · Net {money(r.net_paid)}</div>
      </div>
      {portions.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {portions.map(([k]) => (
            <button key={k} type="button" onClick={() => pick(k)} data-testid={`refund-portion-${k}`} className="font-label text-[0.6rem] px-3 py-1 border"
                    style={{ borderColor: portion === k ? "var(--ss-rust)" : "var(--ss-border)", background: portion === k ? "var(--ss-rust)" : "transparent", color: portion === k ? "#F5F1E8" : "var(--ss-cream)" }}>
              {PORTION_LABEL[k]}
            </button>
          ))}
        </div>
      )}
      {p && (
        <div className="text-sm space-y-1" style={{ color: "var(--ss-cream-dim)" }}>
          <div>{r.executive ? "Executive package" : PORTION_LABEL[portion]} — policy: {p.policy}{portion === "service" && r.days_before_event !== null && ` (${r.days_before_event} days out)`}</div>
          {portion === "service" && <div>Retainer paid (non-refundable): {money(r.retainer_paid)} · Paid beyond retainer: {money(r.paid_beyond_retainer)}</div>}
          <div>Available to refund: <strong data-testid="refund-available">{money(p.available)}</strong> · Already refunded: {money(p.refunded)}</div>
          <div style={{ color: "var(--ss-cream)" }}>Policy recommendation: <strong data-testid="refund-recommended">{p.recommended == null ? "Manual review — no automatic amount" : money(p.recommended)}</strong></div>
          {r.executive && <div className="text-xs" data-testid="refund-exec-note">Custom products status: {r.supply_status || "not started"}. Decide the amount from the nightclub/event terms for the rental/service portion and the Social Supply terms for products in production.</div>}
        </div>
      )}
      {p && p.available > 0 && !review && (
        <div className="mt-3 flex flex-wrap gap-2 items-end">
          <input className="editorial-input text-sm" style={{ width: "8rem", flex: "none" }} type="number" step="0.01" min="0" max={p.available}
                 value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount" />
          <input className="editorial-input flex-1 text-sm" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="refund-reason" />
          <button className="btn-outline" disabled={!valid} onClick={openReview} data-testid="refund-submit">Approve & Refund</button>
          {amt > p.available && <div className="w-full text-xs" style={{ color: "#E07A7A" }}>Amount exceeds what can be refunded ({money(p.available)}).</div>}
          {amt === 0 && <div className="w-full text-xs" style={{ color: "var(--ss-muted)" }}>Enter an amount and a reason to continue.</div>}
        </div>
      )}
      {review && (
        <div className="mt-4 p-4 border space-y-2 text-sm" style={{ borderColor: "var(--ss-gold)" }} data-testid="refund-review">
          <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-gold)" }}>Confirm refund</div>
          <div>Refund <strong data-testid="refund-review-amount">{money(amt)}</strong> of {PORTION_LABEL[portion]} to {b.name} ({b.email})</div>
          <div>Reason: “{reason.trim()}”</div>
          <div>Processed through Stripe against the original payment. {amt < availAtReview.current ? `Partial refund — ${money(availAtReview.current - amt)} of this portion remains paid.` : "Full refund of this portion."}</div>
          <div className="text-xs" style={{ color: "var(--ss-muted)" }}>Other portions of the booking and the booking status are not changed.</div>
          {error && <div className="text-xs" style={{ color: "#E07A7A" }} data-testid="refund-error">{error}</div>}
          <div className="flex gap-2 pt-2">
            <button className="btn-primary" disabled={busy} onClick={confirm} data-testid="refund-confirm">{busy ? "Processing…" : error ? "Retry refund" : "Confirm & process refund"}</button>
            <button className="btn-outline" disabled={busy} onClick={() => setReview(false)} data-testid="refund-cancel">Back</button>
          </div>
        </div>
      )}
      {(b.refunds || []).some((x) => ["pending", "requires_action"].includes(x.status)) && (
        <button className="btn-outline mt-3" onClick={refresh} data-testid="refund-refresh">Refresh refund status</button>
      )}
      <History b={b} />
    </div>
  );
};
